import React, { useState, useEffect } from 'react';
import { useQuizContext } from '../context/QuizContext';
import LoadingScreen from '../utils/LoadingScreen';
import {usePreloadImages} from '../utils/usePreloadImages';

const Quiz = () => {
  const { 
    questions,
    currentQuestion,
    answers,
    saveAnswer,
    nextQuestion,
    previousQuestion,
    setCurrentStep,
    isSubmitting
  } = useQuizContext();
  
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const buttonColor = '#c84357';
  const progressColor = '#c84357';
  usePreloadImages(questions);
  // Muestra la pantalla de registro si no hay preguntas
  useEffect(() => {
    if (!questions.length) setCurrentStep(1);
    else {
      const timer = setTimeout(() => setIsLoading(false), 500);
      return () => clearTimeout(timer);
    }
  }, [questions, setCurrentStep]);
  
  // Restaura la opción ya seleccionada al cambiar de pregunta
  useEffect(() => {
    if (!questions.length) return;
    const qId = questions[currentQuestion].id;
    const saved = answers.find(a => a.questionId === qId);
    setSelectedOption(saved?.answerId || null);
  }, [currentQuestion, questions, answers]);

  // Loading inicial
  if (isLoading || !questions.length) {
    return (
      <div 
        className="flex items-center justify-center min-h-screen p-4"
        style={{
          backgroundImage: 'url(/assets/images/background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="max-w-md w-full rounded-xl shadow-lg bg-[#030645] p-8">
          <div className="flex justify-center mb-4">
            <img 
              src="/assets/logos/logo3.jpeg" 
              alt="COMMERK" 
              className="h-28 animate-pulse"
            />
          </div>
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-white mx-auto mb-4"></div>
            <p className="text-xl text-white">Cargando preguntas…</p>
          </div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  // Al seleccionar cualquier opción, guardo y avanzo
  const handleOptionSelect = (optionId) => {
    if (isSubmitting) return;

    const opt = question.options.find(o => o.id === optionId);
    setSelectedOption(optionId);
    saveAnswer(question.id, optionId, question.question, opt.text);

    // Avanzar siempre (si es la última, dispara submit)
    setTimeout(() => nextQuestion(), 300);
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-4"
      style={{
        backgroundImage: "url(/assets/images/background.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-md rounded-xl shadow-lg bg-white p-4">
        {/* Barra de progreso */}
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-black">
              Paso {currentQuestion + 1} de {questions.length}
            </span>
            <span className="text-black">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, backgroundColor: progressColor }}
            />
          </div>
        </div>

        {/* Pregunta */}
        <h2 className="text-xl font-bold text-center mb-4 text-black">
          {question.question}
        </h2>

        {/* Opciones: al clic, guardan y avanzan */}
        <div className="grid grid-cols-1 gap-2 mb-4">
          {question.options.map(option => (
            <div
              key={option.id}
              onClick={() => handleOptionSelect(option.id)}
              className={`
                cursor-pointer transform transition duration-200
                hover:scale-105 rounded-lg overflow-hidden
                bg-gray-100 shadow-sm p-2
                ${selectedOption === option.id ? "ring-2 ring-red-500" : ""}
                ${isSubmitting ? "pointer-events-none opacity-70" : ""}
              `}
            >
              <img
                src={option.imageUrl}
                alt={option.text}
                className="w-20 h-16 object-cover rounded-md mx-auto mb-2"
              />
              <div className="text-center font-bold text-sm text-[#FF5733] mb-1">
                {option.category}
              </div>
              <p className="text-center text-xs text-black leading-snug">
                {option.text}
              </p>
            </div>
          ))}
        </div>

        {/* Solo botón de “anterior” */}
        <div className="flex justify-start">
          <button
            onClick={previousQuestion}
            disabled={currentQuestion === 0 || isSubmitting}
            className="text-black hover:text-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Pantalla de loading al enviar resultados */}
      {isSubmitting && (
        <LoadingScreen message="¡Gracias por participar! Nuestra IA🤖 esta procesando tus respuestas… en breve tendrás tu resultado." videoUrl="https://videos-commerk.s3.us-east-2.amazonaws.com/Video-Regala-Experiencias-_1_.webm" />
      )}
    </div>
  );
};

export default Quiz;
